import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import qs from 'qs';
import printPdf from 'utils/printPdf';
import { uniq, isArray } from 'lodash';
import { Helmet } from 'react-helmet';

import { getFormValues } from 'redux-form';

import withStyles from '@material-ui/core/styles/withStyles';
import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import DocumentPreviewFillForm from 'components/common/DocumentPreviewFillForm';
import TemplatePreviewFillForm from 'components/settings/TemplatePreviewFillForm';
import Link from 'components/common/Link';

import { fetchOrder } from 'redux/modules/orders';
import { fetchArchiveOrder } from 'redux/modules/archiveOrders';
import { fetchTemplates } from 'redux/modules/templates';
import { createPatientDocument } from 'redux/modules/patientsDocuments';
import { fetchBusiness } from 'redux/modules/business';
import { addNotification } from 'redux/modules/notifications';
import { fetchSystemSettings } from 'redux/modules/systemSettings';
import { fetchUser } from 'redux/modules/users';

import documentToPDF from 'utils/documentToPDF';
import templateToPDF from 'utils/templateToPDF';
import formatOrderNumber from 'utils/formatOrderNumber';

import { appTitle, titleSeparator } from 'config/helmetSettings';
import documents from 'config/documents';

const styles = theme => ({
  subtitleItem: {
    display: 'inline-block',
    marginRight: 8,
    marginBottom: 8,
  },
});

class OrderPrint extends Component {
  constructor(props) {
    super(props);

    this.state = {
      navigationSections: [],
      documentImages: null,
    };

    this.onTemplatesRequest = this.onTemplatesRequest.bind(this);
    this.onSignatureChange = this.onSignatureChange.bind(this);
    this.onNavigationUpdate = this.onNavigationUpdate.bind(this);
    this.importDependencies = this.importDependencies.bind(this);
  }

  componentDidMount() {
    const { actions, match, archive, account, location, history } = this.props;
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({ isMounted: true });
    this.importDependencies();
    if (window.historyState && !location.state) {
      history.replace({
        pathname: location.pathname,
        search: location.search,
        state: window.historyState,
      });
    }
    if (archive) {
      actions.fetchArchiveOrder({
        ID: match.params.id,
        triggerLoading: true,
      });
    } else {
      actions.fetchOrder({
        ID: match.params.id,
        triggerLoading: true,
      });
    }
    actions.fetchBusiness({
      triggerLoading: true,
    });
    actions.fetchSystemSettings({
      triggerLoading: true,
    });
    this.onTemplatesRequest();
    actions.fetchUser({
      ID: account.ID,
      triggerLoading: true,
    });
  }

  componentWillReceiveProps(nextProps) {
    const {
      order,
      loading,
      location,
      history,
      variant,
    } = this.props;
    if (loading > 0
      && nextProps.loading === 0
      && ((variant === 'document' && !nextProps.document)
        || (variant === 'template' && !nextProps.template))) {
      if (nextProps.order) {
        history.replace(`/workflow/order/view/${order.ID}`);
      } else {
        history.replace('/workflow');
      }
    }

    if (nextProps.order) {
      this.onNavigationUpdate({
        order: nextProps.order,
        templates: nextProps.templates,
        signature: location.state && location.state.signature
          ? location.state.signature : null,
        signatureDate: location.state && location.state.signatureDate
          ? location.state.signatureDate : null,
        orders: location.state && location.state.orders
          ? location.state.orders : null,
      });
    }
  }

  componentWillUnmount() {
    this.setState({ isMounted: false });
  }

  onTemplatesRequest() {
    const { actions, location, match: { params: { template } } } = this.props;
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    let selectedTemplates = Object.keys(params)
      .filter(item => item.indexOf('template') !== -1)
      .map(item => Number(item.replace('template-', '')));
    selectedTemplates = template
      ? uniq([Number(template), ...selectedTemplates]) : selectedTemplates;
    actions.fetchTemplates({
      triggerLoading: true,
      filters: [{
        name: 'ID',
        operation: 'in',
        val: selectedTemplates,
      }],
    });
  }

  onSignatureChange(signature) {
    this.onNavigationUpdate({
      order: this.props.order,
      templates: this.props.templates,
      signature,
      orders: this.props.location.state && this.props.location.state.orders
        ? this.props.location.state.orders : null,
    });
  }

  onNavigationUpdate({
    order,
    templates,
    signature,
    signatureDate,
    orders,
  }) {
    const { location } = this.props;
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    const navigationSections = Object.keys(params).reduce((accumulator, currentValue) => {
      if (currentValue.indexOf('document') !== -1) {
        const currentDocument = documents
          .find(item => item.ID === Number(currentValue.replace('document-', '')));
        const currentItems = isArray(params[currentValue])
          ? params[currentValue].map((orderLine) => {
            const orderLineHCPCSCodes = params[currentValue].length > 1
              ? order['Order Line']
                .filter(item => orderLine.split('-').map(orderLineItem => Number(orderLineItem)).indexOf(item.ID) !== -1)
                .map(item => item['HCPC S Code']['HCPC S Code']).join(', ')
              : null;
            return {
              title: orderLineHCPCSCodes ? `${currentDocument.Name} (${orderLineHCPCSCodes})` : currentDocument.Name,
              id: `Document_${currentDocument.ID}_${orderLine}`,
              href: `/workflow/order/print/${order.ID}/document/${currentDocument.ID}/${orderLine}`,
              state: {
                ...signature ? { signature } : {},
                ...signatureDate ? { signatureDate } : {},
                ...orders ? { orders } : {},
              },
            };
          })
          : [{
            title: currentDocument.Name,
            id: `Document_${currentDocument.ID}`,
            href: `/workflow/order/print/${order.ID}/document/${currentDocument.ID}`,
            ...signature ? {
              state: {
                ...signature ? { signature } : {},
                ...signatureDate ? { signatureDate } : {},
                ...orders ? { orders } : {},
              },
            } : {},
          }];
        switch (currentDocument.Type) {
          case 'General':
            return [
              {
                title: 'General',
                items: [
                  ...accumulator[0] && accumulator[0].items ? accumulator[0].items : [],
                  ...currentItems,
                ],
              },
              ...accumulator.slice(1),
            ];
          case 'CMS':
            return [
              ...accumulator.slice(0, 1),
              {
                title: 'CMS',
                items: [
                  ...accumulator[1] && accumulator[1].items ? accumulator[1].items : [],
                  ...currentItems,
                ],
              },
              ...accumulator.slice(2),
            ];
          case 'Delivery Ticket':
            return [
              ...accumulator.slice(0, 2),
              {
                title: 'Delivery Tickets',
                items: [
                  ...accumulator[2] && accumulator[2].items ? accumulator[2].items : [],
                  ...currentItems,
                ],
              },
              ...accumulator.slice(3),
            ];
          case 'Form 1500':
            return [
              ...accumulator.slice(0, 3),
              {
                title: '1500 Forms',
                items: [
                  ...accumulator[3] && accumulator[3].items ? accumulator[3].items : [],
                  ...currentItems,
                ],
              },
              ...accumulator.slice(4),
            ];
          case 'Prior Authorization Template':
            return [
              ...accumulator.slice(0, 4),
              {
                title: 'Prior Authorization Templates',
                items: [
                  ...accumulator[4] && accumulator[4].items ? accumulator[4].items : [],
                  ...currentItems,
                ],
              },
              ...accumulator.slice(5),
            ];
          case 'Assessment Form':
            return [
              ...accumulator.slice(0, 5),
              {
                title: 'Assessment Forms',
                items: [
                  ...accumulator[5] && accumulator[5].items ? accumulator[5].items : [],
                  ...currentItems,
                ],
              },
              ...accumulator.slice(6),
            ];
          default:
            return accumulator;
        }
      }
      if (templates.length > 0 && currentValue.indexOf('template') !== -1) {
        const currentTemplate = templates
          .find(item => item.ID === Number(currentValue.replace('template-', '')));
        return [
          ...accumulator.slice(0, 6),
          {
            title: 'Templates',
            items: [
              ...accumulator[6] && accumulator[6].items ? accumulator[6].items : [],
              {
                title: currentTemplate.Name,
                id: `Template_${currentTemplate.ID}`,
                href: `/workflow/order/print/${order.ID}/template/${currentTemplate.ID}`,
                state: {
                  ...signature ? { signature } : {},
                  ...signatureDate ? { signatureDate } : {},
                  ...orders ? { orders } : {},
                },
              },
            ],
          },
        ];
      }
      return accumulator;
    }, new Array(7));
    this.setState({ navigationSections });
  }

  async importDependencies() {
    const { document, variant, appInfo } = this.props;
    if (variant === 'document') {
      const documentImages = [];
      const pageImagesPromises = document.Pages.map((page, index) => (
        import(`../../${page.imageUrl}`)
          .then((image) => {
            documentImages[index] = image.default;
          })));
      Promise.all(pageImagesPromises).then(() => {
        if (this.state.isMounted) {
          this.setState({
            documentImages,
          });
        }
      });
    }
  }

  render() {
    const {
      order,
      loading,
      variant,
      template,
      document,
      business,
      systemSettings,
      actions,
      appInfo,
      account,
      location,
      history,
      match: { params },
      classes,
      currentUser,
      printNavigationFormValues,
      selectedFormValues,
      templates,
      record,
    } = this.props;
    const orderLine = params.orderLine
      ? params.orderLine.split('-').map(item => Number(item))
      : null;
    const { navigationSections, documentImages } = this.state;
    const documentWithImages = document && documentImages ? {
      ...document,
      Pages: document.Pages.map((page, index) => ({
        ...page,
        image: documentImages[index],
      })),
    } : null;
    let initialValues = {};
    let signatureFieldNames = [];
    if (variant === 'document' && order
      && Object.keys(business).length > 0 && Object.keys(systemSettings).length > 0) {
      const fields = document.Pages.reduce((accumulator, currentValue) => ([
        ...accumulator,
        ...currentValue.fields ? currentValue.fields : [],
      ]), []);
      signatureFieldNames = fields
        .filter(item => item.type === 'signature' && item.getValue)
        .map(item => item.name);
      initialValues = fields.reduce((accumulator, currentValue) => ({
        ...accumulator,
        ...{ [currentValue.name]:
          currentValue.getValue
            ? currentValue.getValue({
              patient: order.Patient,
              business,
              systemSettings,
              insurance: order['Primary Insurance']['Insurance Type'] === 'Self'
                ? {
                  ...order['Primary Insurance'],
                  'Insurance Info': {
                    ...order['Primary Insurance']['Insurance Info'],
                    'Payer Name': 'Self Payer',
                  },
                } : order['Primary Insurance'],
              facility: order.Facility,
              diagnosis: order.Diagnosis,
              orderLine: orderLine
                ? order['Order Line'].filter(item => orderLine.indexOf(item.ID) !== -1)
                : order['Order Line'],
              orderInsuranceParam: order['Order Insurance Param'],
              prescriber: order.Prescriber,
              referralSource: order['Referral Source'],
              orderNotes: (order && order.Notes) ? order.Notes : [],
              ...location.state && location.state.signature ? {
                signature: location.state.signature,
              } : {},
              ...location.state && location.state.signatureDate ? {
                signatureDate: location.state.signatureDate,
              } : {},
              ...location.state && location.state.orders
                ? { orders: location.state.orders } : {},
              account: {
                ...account,
                ...currentUser ? { ...currentUser } : {},
              },
            }) : null },
      }), {
        Name: document.Name,
        'Show Charge': true,
        'Show Background Template': true,
      });
    }
    const onlyPrintHistoryRecord = record.filter(item => item.pathname.indexOf('print') === -1).length === 0;
    return (
      <Layout
        variant="print"
        navigationSections={loading === 0 ? navigationSections.filter(item => item) : null}
        {...order ? {
          titleText: `Order ${formatOrderNumber(order.ID)}`,
        } : {}}
        {...order && location.state && location.state.orders
          ? { subtitle: (
            <Fragment>
              {Object.keys(location.state.orders).filter(item => Number(item) !== Number(order.ID))
                .map((item) => {
                  const editPreviewHrefPrefix = `/workflow/order/print/${item}`;
                  let editPreviewHrefStem = '';
                  if (Object.keys(location.state.orders[item]).join().indexOf('document') !== -1) {
                    const firstDocumentValue = Object.keys(location.state.orders[item])
                      .find(item => item.indexOf('document') !== -1);
                    const firstDocumentID = firstDocumentValue.replace('document-', '');
                    const orderLine = location.state.orders[item][firstDocumentValue];
                    editPreviewHrefStem = orderLine
                      ? `/document/${firstDocumentID}/${orderLine[0]}`
                      : `/document/${firstDocumentID}`;
                  } else if (Object.keys(location.state.orders[item]).join().indexOf('template') !== -1) {
                    editPreviewHrefStem =
                      `/template/${Object.keys(location.state.orders[item])
                        .find(item => item.indexOf('template') !== -1).replace('template-', '')}`;
                  }
                  const editPreviewHref = `${editPreviewHrefPrefix}${editPreviewHrefStem}`;
                  const editPreviewHrefParams = qs.stringify(location.state.orders[item], {
                    encode: false, addQueryPrefix: true,
                  });
                  return(
                  <div className={classes.subtitleItem} key={item}>
                    <Link
                      color="primary"
                      underline
                      onClick={() => {
                        history.push({
                          pathname: editPreviewHref,
                          search: editPreviewHrefParams,
                          state: {
                            orders: location.state.orders,
                            signature: location.state.signature,
                            signatureDate: location.state.signatureDate,
                          },
                        });
                      }}
                    >
                      #{formatOrderNumber(item)}
                    </Link>
                  </div>
                )})}
            </Fragment>
          )}
          : {}}
      >
        {(loading > 0 || !order) ? (
          <Progress />
        ) : (
          <Fragment>
            {(variant === 'document' && document && documentImages) &&
              <Fragment>
                <Helmet>
                  <title>
                    {appTitle} {titleSeparator} Print {
                      formatOrderNumber(order.ID)} {titleSeparator} {document.Name}
                  </title>
                </Helmet>
                <DocumentPreviewFillForm
                  form={orderLine
                    ? `${order.ID}-order-${orderLine.join('_')}-orderLine-${document.ID}-documentPreviewFill`
                    : `${order.ID}-order-${document.ID}-documentPreviewFill`}
                  variant={onlyPrintHistoryRecord ? '_blank' : '_self'}
                  document={documentWithImages}
                  initialValues={initialValues}
                  business={business}
                  order={order}
                  appInfo={appInfo}
                  signatureFieldNames={signatureFieldNames}
                  onSignatureChange={this.onSignatureChange}
                  onSubmitCustom={printOnSubmitSuccess =>
                    (values) => {
                      const printNavigationKeys = printNavigationFormValues
                        ? Object.keys(printNavigationFormValues).filter(item => printNavigationFormValues[item] === true)
                        : [];
                      if (printNavigationKeys.length > 0) {
                        let printDocumentName = [];
                        return printNavigationKeys
                          .sort((a, b) => {
                            const documentA = documents.find(item => item.ID === Number(a.split('_')[1]));
                            const documentB = documents.find(item => item.ID === Number(b.split('_')[1]));
                            return (documentA.BatchPrintPosition || 100) - (documentB.BatchPrintPosition || 100);
                          })
                          .reduce((accumulator, currentValue) => {
                            if (currentValue.indexOf('Document') !== -1) {
                              const documentID = Number(currentValue.split('_')[1]);
                              const orderLine = currentValue.split('_')[2]
                                ? currentValue.split('_')[2].split('-').map(item => Number(item))
                                : null;
                              const selectedDocument = documents
                                .find(item => item.ID === documentID);
                              const currentFormName = orderLine
                                ? `${order.ID}-order-${orderLine.join('_')}-orderLine-${documentID}-documentPreviewFill`
                                : `${order.ID}-order-${documentID}-documentPreviewFill`
                              const currentFormValues = selectedFormValues[currentFormName];
                              printDocumentName.push(selectedDocument.Name);
                              return accumulator.then(prevPDF => documentToPDF({
                                inputPDF: prevPDF,
                                document: selectedDocument,
                                patient: order.Patient,
                                business,
                                systemSettings,
                                insurance: order['Primary Insurance']['Insurance Type'] === 'Self'
                                  ? {
                                    ...order['Primary Insurance'],
                                    'Insurance Info': {
                                      ...order['Primary Insurance']['Insurance Info'],
                                      'Payer Name': 'Self Payer',
                                    },
                                  } : order['Primary Insurance'],
                                facility: order.Facility,
                                diagnosis: order.Diagnosis,
                                orderLine: orderLine
                                  ? order['Order Line'].filter(item => orderLine.indexOf(item.ID) !== -1)
                                  : order['Order Line'],
                                orderInsuranceParam: order['Order Insurance Param'],
                                prescriber: order.Prescriber,
                                referralSource: order['Referral Source'],
                                account: {
                                  ...account,
                                  ...currentUser ? { ...currentUser } : {},
                                },
                                appInfo,
                                ...currentFormValues ? { values: currentFormValues } : {},
                              }));
                            }
                            if (currentValue.indexOf('Template') !== -1) {                           
                              const templateID = Number(currentValue.split('_')[1]);
                              const selectedTemplate = templates
                                .find(item => item.ID === templateID);
                              const currentFormName = `${order.ID}-order-${templateID}-templatePreviewFill`
                              const currentFormValues = selectedFormValues[currentFormName];
                              printDocumentName.push(selectedTemplate.Name);
                              return accumulator.then(prevPDF => templateToPDF({
                                inputPDF: prevPDF,
                                template: selectedTemplate,
                                patient: order.Patient,
                                order: order,
                                business,
                                ...location.state && location.state.signature ? {
                                  signature: location.state.signature,
                                } : {},
                                ...location.state && location.state.signatureDate ? {
                                  signatureDate: location.state.signatureDate,
                                } : {},
                                ...location.state && location.state.orders
                                  ? { orders: location.state.orders } : {},
                                ...currentFormValues ? { values: currentFormValues } : {},
                              }));
                            }
                          }, Promise.resolve()).then(PDF => ({
                            blob: PDF.output('blob'),
                            Name: uniq(printDocumentName).join('-'),
                            printOnSubmitSuccess,
                          }));
                      } else {
                        return documentToPDF({
                          document: documentWithImages,
                          patient: order.Patient,
                          business,
                          systemSettings,
                          insurance: order['Primary Insurance']['Insurance Type'] === 'Self'
                            ? {
                              ...order['Primary Insurance'],
                              'Insurance Info': {
                                ...order['Primary Insurance']['Insurance Info'],
                                'Payer Name': 'Self Payer',
                              },
                            } : order['Primary Insurance'],
                          facility: order.Facility,
                          diagnosis: order.Diagnosis,
                          orderLine: orderLine
                            ? order['Order Line'].filter(item => orderLine.indexOf(item.ID) !== -1)
                            : order['Order Line'],
                          orderInsuranceParam: order['Order Insurance Param'],
                          prescriber: order.Prescriber,
                          referralSource: order['Referral Source'],
                          account: {
                            ...account,
                            ...currentUser ? { ...currentUser } : {},
                          },
                          appInfo,
                          values,
                        }).then(PDF => ({
                          blob: PDF.output('blob'),
                          Name: values.Name,
                          printOnSubmitSuccess,
                        }))
                      }
                  }}
                  onSubmitSuccess={(values) => {
                    if (values.printOnSubmitSuccess) {
                      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        window.navigator.msSaveOrOpenBlob(values.blob, `${values.Name}.pdf`);
                      } else {
                        printPdf(values.blob);
                      }
                    } else {
                      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        const fileBlob = values.blob;
                        fileBlob.lastModifiedDate = new Date();
                        actions.createPatientDocument({
                          values: {
                            'Record Number': order.Patient['Record Number'],
                            Document: fileBlob,
                            Name: `${values.Name}.pdf`,
                          },
                        });
                      } else {
                        const file = new File(
                          [values.blob],
                          `${values.Name}.pdf`,
                          { type: 'application/pdf', lastModified: Date.now() },
                        );
                        actions.createPatientDocument({
                          values: {
                            'Record Number': order.Patient['Record Number'],
                            Document: file,
                          },
                        });
                      }
                      actions.addNotification({
                        message: `"${values.Name}.pdf" was saved to Documents in ${order.Patient['First Name']}, ${order.Patient['Last Name']} chart`,
                        variant: 'success',
                      });
                    }
                  }}
                />
              </Fragment>
            }
            {(variant === 'template' && template) &&
              <Fragment>
                <Helmet>
                  <title>
                    {appTitle} {titleSeparator} Print {
                      formatOrderNumber(order.ID)} {titleSeparator} {template.Name}
                  </title>
                </Helmet>
                <TemplatePreviewFillForm
                  form={`${order.ID}-order-${template.ID}-templatePreviewFill`}
                  variant={onlyPrintHistoryRecord ? '_blank' : '_self'}
                  template={template}
                  patient={order.Patient}
                  order={order}
                  business={business}
                  onSignatureChange={this.onSignatureChange}
                  initialValues={{
                    Name: template.Name,
                    ...location.state && location.state.signature
                      ? { Questions: template.Questions.map((currentValue) => {
                        if (currentValue['Question Type'] === 'Signature') {
                          return location.state.signature;
                        }
                        return null;
                      }) } : {},
                  }}
                  onSubmitCustom={printOnSubmitSuccess =>
                    (values) => {
                      const printNavigationKeys = printNavigationFormValues
                        ? Object.keys(printNavigationFormValues).filter(item => printNavigationFormValues[item] === true)
                        : [];
                      if (printNavigationKeys.length > 0) {
                        let printDocumentName = [];
                        return printNavigationKeys
                          .sort((a, b) => {
                            const documentA = documents.find(item => item.ID === Number(a.split('_')[1]));
                            const documentB = documents.find(item => item.ID === Number(b.split('_')[1]));
                            return (documentA.BatchPrintPosition || 100) - (documentB.BatchPrintPosition || 100);
                          })
                          .reduce((accumulator, currentValue) => {
                            if (currentValue.indexOf('Document') !== -1) {
                              const documentID = Number(currentValue.split('_')[1]);
                              const orderLine = currentValue.split('_')[2]
                                ? currentValue.split('_')[2].split('-').map(item => Number(item))
                                : null;
                              const selectedDocument = documents
                                .find(item => item.ID === documentID);
                              const currentFormName = orderLine
                                ? `${order.ID}-order-${orderLine.join('_')}-orderLine-${documentID}-documentPreviewFill`
                                : `${order.ID}-order-${documentID}-documentPreviewFill`
                              const currentFormValues = selectedFormValues[currentFormName];
                              printDocumentName.push(selectedDocument.Name);
                              return accumulator.then(prevPDF => documentToPDF({
                                inputPDF: prevPDF,
                                document: selectedDocument,
                                patient: order.Patient,
                                business,
                                systemSettings,
                                insurance: order['Primary Insurance']['Insurance Type'] === 'Self'
                                  ? {
                                    ...order['Primary Insurance'],
                                    'Insurance Info': {
                                      ...order['Primary Insurance']['Insurance Info'],
                                      'Payer Name': 'Self Payer',
                                    },
                                  } : order['Primary Insurance'],
                                facility: order.Facility,
                                diagnosis: order.Diagnosis,
                                orderLine: orderLine
                                  ? order['Order Line'].filter(item => orderLine.indexOf(item.ID) !== -1)
                                  : order['Order Line'],
                                orderInsuranceParam: order['Order Insurance Param'],
                                prescriber: order.Prescriber,
                                referralSource: order['Referral Source'],
                                account: {
                                  ...account,
                                  ...currentUser ? { ...currentUser } : {},
                                },
                                appInfo,
                                ...currentFormValues ? { values: currentFormValues } : {},
                              }));
                            }
                            if (currentValue.indexOf('Template') !== -1) {                           
                              const templateID = Number(currentValue.split('_')[1]);
                              const selectedTemplate = templates
                                .find(item => item.ID === templateID);
                              const currentFormName = `${order.ID}-order-${templateID}-templatePreviewFill`
                              const currentFormValues = selectedFormValues[currentFormName];
                              printDocumentName.push(selectedTemplate.Name);
                              return accumulator.then(prevPDF => templateToPDF({
                                inputPDF: prevPDF,
                                template: selectedTemplate,
                                patient: order.Patient,
                                order: order,
                                business,
                                ...location.state && location.state.signature ? {
                                  signature: location.state.signature,
                                } : {},
                                ...location.state && location.state.signatureDate ? {
                                  signatureDate: location.state.signatureDate,
                                } : {},
                                ...location.state && location.state.orders
                                  ? { orders: location.state.orders } : {},
                                ...currentFormValues ? { values: currentFormValues } : {},
                              }));
                            }
                          }, Promise.resolve()).then(PDF => ({
                            blob: PDF.output('blob'),
                            Name: uniq(printDocumentName).join('-'),
                            printOnSubmitSuccess,
                          }));
                      } else {
                        return templateToPDF({
                          template,
                          patient: order.Patient,
                          order: order,
                          business,
                          ...location.state && location.state.signature ? {
                            signature: location.state.signature,
                          } : {},
                          ...location.state && location.state.signatureDate ? {
                            signatureDate: location.state.signatureDate,
                          } : {},
                          ...location.state && location.state.orders
                            ? { orders: location.state.orders } : {},
                          values,
                        }).then(PDF => ({
                          blob: PDF.output('blob'),
                          Name: values.Name,
                          printOnSubmitSuccess,
                        }));
                      }
                  }}
                  onSubmitSuccess={(values) => {
                    if (values.printOnSubmitSuccess) {
                      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        window.navigator.msSaveOrOpenBlob(values.blob, `${values.Name}.pdf`);
                      } else {
                        printPdf(values.blob);
                      }
                    } else {
                      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        const fileBlob = values.blob;
                        fileBlob.lastModifiedDate = new Date();
                        actions.createPatientDocument({
                          values: {
                            'Record Number': order.Patient['Record Number'],
                            Document: fileBlob,
                            Name: `${values.Name}.pdf`,
                          },
                        });
                      } else {
                        const file = new File(
                          [values.blob],
                          `${values.Name}.pdf`,
                          { type: 'application/pdf', lastModified: Date.now() },
                        );
                        actions.createPatientDocument({
                          values: {
                            'Record Number': order.Patient['Record Number'],
                            Document: file,
                          },
                        });
                      }
                      actions.addNotification({
                        message: `"${values.Name}.pdf" was saved to Documents in ${order.Patient['First Name']}, ${order.Patient['Last Name']} chart`,
                        variant: 'success',
                      });
                    }
                  }}
                />
              </Fragment>
            }
          </Fragment>
        )}
      </Layout>
    );
  }
}

OrderPrint.propTypes = {
  variant: PropTypes.oneOf(['document', 'template']).isRequired,
  order: PropTypes.shape({
    ID: PropTypes.number.isRequired,
    Patient: PropTypes.shape({
      'Record Number': PropTypes.number.isRequired,
    }).isRequired,
    'Order Line': PropTypes.arrayOf(PropTypes.shape({
      ID: PropTypes.number.isRequired,
    })),
  }),
  document: PropTypes.shape({
    ID: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
  }),
  templates: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
  })).isRequired,
  template: PropTypes.shape({
    ID: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
  }),
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  systemSettings: PropTypes.object.isRequired,
  loading: PropTypes.number.isRequired,
  actions: PropTypes.shape({
    fetchOrder: PropTypes.func.isRequired,
    fetchArchiveOrder: PropTypes.func.isRequired,
    fetchTemplates: PropTypes.func.isRequired,
    createPatientDocument: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    fetchBusiness: PropTypes.func.isRequired,
    fetchSystemSettings: PropTypes.func.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
      document: PropTypes.string,
      template: PropTypes.string,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    state: PropTypes.object,
  }).isRequired,
  appInfo: PropTypes.shape({
    'APP NAME': PropTypes.string,
  }).isRequired,
};

OrderPrint.defaultProps = {
  order: null,
  document: null,
  template: null,
  business: {},
};

const mapStateToProps = (store, ownProps) => {
  const printNavigationFormValues = getFormValues('printNavigationForm')(store);
  let order;
  if (ownProps.archive) {
    order = store.archiveOrders.data.length > 0 && !store.form.ordersFilter
      ? store.archiveOrders.data.find(order => order.ID === Number(ownProps.match.params.id))
      : null;
  } else {
    order = store.orders.data.length > 0 && !store.form.ordersFilter
      ? store.orders.data.find(order => order.ID === Number(ownProps.match.params.id))
      : null;
  }
  const document = documents.find(item => item.ID === Number(ownProps.match.params.document));
  const printNavigationKeys = printNavigationFormValues
    ? Object.keys(printNavigationFormValues) : [];
  let selectedFormValues = {};
  if (printNavigationKeys.length > 0) {
    selectedFormValues = printNavigationKeys
      .filter(item => printNavigationFormValues[item] === true)
      .reduce((accumulator, currentValue) => {
        if (currentValue.indexOf('Document') !== -1) {
          const documentID = Number(currentValue.split('_')[1]);
          const orderLine = currentValue.split('_')[2]
            ? currentValue.split('_')[2].split('-').map(item => Number(item))
            : null;
          const currentFormName = orderLine
          ? `${order.ID}-order-${orderLine.join('_')}-orderLine-${documentID}-documentPreviewFill`
          : `${order.ID}-order-${documentID}-documentPreviewFill`
          const currentFormValues = getFormValues(currentFormName)(store);
          return {
            ...accumulator,
            ...currentFormValues ? { [currentFormName]: currentFormValues } : {},
          };
        }
        if (currentValue.indexOf('Template') !== -1) {
          const templateID = Number(currentValue.split('_')[1]);
          const currentFormName = `${order.ID}-order-${templateID}-templatePreviewFill`
          const currentFormValues = getFormValues(currentFormName)(store);
          return {
            ...accumulator,
            ...currentFormValues ? { [currentFormName]: currentFormValues } : {},
          };
        }
        return accumulator;
      }, {});
  }
  return {
    order,
    // !store.form.ordersFilter - check if order lines are not filtered by State
    document,
    templates: store.templates.data,
    template: store.templates.data.find(item => item.ID === Number(ownProps.match.params.template)),
    business: store.business.data,
    systemSettings: store.systemSettings.data,
    appInfo: store.appInfo,
    loading: store.orders.loading
      + store.archiveOrders.loading
      + store.templates.loading
      + store.business.loading
      + store.systemSettings.loading
      + store.users.loading,
    account: store.account,
    currentUser: store.users.data.find(item => item.ID === store.account.ID),
    printNavigationFormValues,
    selectedFormValues,
    record: store.routerParams.record,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchOrder,
    fetchArchiveOrder,
    fetchTemplates,
    createPatientDocument,
    fetchBusiness,
    addNotification,
    fetchSystemSettings,
    fetchUser,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(OrderPrint));



// WEBPACK FOOTER //
// ./src/pages/workflow/OrderPrint.js